import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { messages } from '../onboarding/onboarding-messages';
import { Typography } from '../atomic-components/typography';
import { Divider } from '../atomic-components/divider';
import { CheckBox, booleanToCheckedState } from '../atomic-components/check-box';
import { ArrowButton } from '../atomic-components/arrow-button';

export interface CarouselPage {
  title: string;
  content: React.ReactNode;
}

export interface CarouselProps {
  pages: CarouselPage[];
  isSkipChecked: boolean;
  onCheckBoxClick: () => void;
  hideSkipMessage: boolean;
}

export const Carousel: React.FC<CarouselProps> = ({ isSkipChecked, onCheckBoxClick, pages, hideSkipMessage }) => {
  const intl = useIntl();
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const page = pages[currentPageNumber - 1];

  const disablePrevious = currentPageNumber === 1;
  const disableNext = currentPageNumber === pages.length;

  const handlePrevButton = useCallback((): void => {
    if (disablePrevious) return;
    setCurrentPageNumber(currentPageNumber - 1);
  }, [currentPageNumber, disablePrevious]);

  const handleNextButton = useCallback((): void => {
    if (disableNext) return;
    setCurrentPageNumber(currentPageNumber + 1);
  }, [currentPageNumber, disableNext]);

  useEffect(() => {
    const onArrows = (event: KeyboardEvent): void => {
      if (event.defaultPrevented) {
        return;
      }

      const key = event.key;

      if (key === 'ArrowLeft') {
        handlePrevButton();
      }
      if (key === 'ArrowRight') {
        handleNextButton();
      }
    };

    document.addEventListener('keyup', onArrows);
    return () => document.removeEventListener('keyup', onArrows);
  }, [handlePrevButton, handleNextButton]);

  return (
    <S.Container>
      <S.ModalTitle>{page.title}</S.ModalTitle>
      <S.ModalContent>{page.content}</S.ModalContent>
      <S.Divider />
      <S.Footer hideSkipMessage={hideSkipMessage}>
        {!hideSkipMessage && (
          <S.Check>
            <CheckBox
              dataTestId="carousel-check-box"
              checked={booleanToCheckedState(isSkipChecked)}
              variant="primary"
              onCheck={onCheckBoxClick}
            />
            <S.SkipMessage>{intl.formatMessage(messages.modalCheck)}</S.SkipMessage>
          </S.Check>
        )}
        <S.ButtonSection>
          <S.ArrowPrevButton
            data-testid="onboarding-previous"
            onClick={handlePrevButton}
            variant="left"
            disabled={disablePrevious}
          />
          <S.ArrowNextButton
            data-testid="onboarding-next"
            onClick={handleNextButton}
            variant="right"
            disabled={disableNext}
          />
        </S.ButtonSection>
      </S.Footer>
    </S.Container>
  );
};

const S = {
  Container: styled.div`
    width: 800px;
    display: flex;
    flex-direction: column;
    user-select: none; // prevent from auto-selection on Safari
  `,
  ModalTitle: styled(Typography.H2)`
    margin-bottom: 24px;
  `,
  ModalContent: styled.div`
    height: 446px;
    display: flex;
    justify-content: center;
  `,
  Divider: styled(Divider)`
    width: 848px;
    margin-left: -24px;
  `,
  Footer: styled.div<{ hideSkipMessage: boolean }>`
    display: flex;
    flex-direction: row;
    justify-content: ${({ hideSkipMessage }) => (hideSkipMessage ? 'flex-end' : 'space-between')};
    width: 100%;
  `,
  Check: styled.div`
    font-size: 14px;
    font-weight: 400;
    color: ${({ theme }) => theme.algoanBlue};
    display: flex;
    flex-direction: row;
    align-items: center;
  `,
  SkipMessage: styled.div`
    margin-left: 16px;
  `,
  ButtonSection: styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
  `,
  ArrowPrevButton: styled(ArrowButton)``,
  ArrowNextButton: styled(ArrowButton)`
    margin-left: 16px;
  `,
};
